import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { Bowtie } from "./icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const goldFocus =
  "bg-ink-700 border border-white/10 focus:border-gold focus-visible:ring-1 focus-visible:ring-gold/60 focus-visible:ring-offset-0 text-white placeholder:text-white/35";

export default function BookingModal({ open, onOpenChange, packages = [], initialPackage }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    package_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialPackage) {
      setForm((f) => ({ ...f, package_interest: initialPackage }));
    }
  }, [initialPackage]);

  const handleChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!validEmail(form.email)) return toast.error("Please enter a valid email");
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Strategy call request sent — we'll be in touch within 24 hours.");
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", business: "", package_interest: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="bg-ink-800 border border-gold/25 text-white max-w-xl rounded-2xl"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center bg-ink-700">
              <Bowtie className="w-5 h-5 text-gold" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.28em] text-gold/80">Strategy Call</span>
          </div>
          <DialogTitle className="font-display text-3xl">
            Book Your <span className="bk-gold-text italic">Call</span>
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Tell us a bit about your business — we'll come back with next steps within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate className="space-y-4 mt-2" data-testid="booking-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bk-name" className="text-xs uppercase tracking-[0.18em] text-white/65">Name *</Label>
              <Input
                id="bk-name" data-testid="booking-name"
                value={form.name} onChange={handleChange("name")}
                placeholder="Your name" className={`mt-2 ${goldFocus}`}
                required
              />
            </div>
            <div>
              <Label htmlFor="bk-email" className="text-xs uppercase tracking-[0.18em] text-white/65">Email *</Label>
              <Input
                id="bk-email" type="email" data-testid="booking-email"
                value={form.email} onChange={handleChange("email")}
                placeholder="you@business.com" className={`mt-2 ${goldFocus}`}
                required
              />
            </div>
            <div>
              <Label htmlFor="bk-phone" className="text-xs uppercase tracking-[0.18em] text-white/65">Phone</Label>
              <Input
                id="bk-phone" data-testid="booking-phone"
                value={form.phone} onChange={handleChange("phone")}
                placeholder="(555) 123-4567" className={`mt-2 ${goldFocus}`}
              />
            </div>
            <div>
              <Label htmlFor="bk-business" className="text-xs uppercase tracking-[0.18em] text-white/65">Business</Label>
              <Input
                id="bk-business" data-testid="booking-business"
                value={form.business} onChange={handleChange("business")}
                placeholder="Company name" className={`mt-2 ${goldFocus}`}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-white/65">Package Interest</Label>
            <Select value={form.package_interest} onValueChange={handleChange("package_interest")}>
              <SelectTrigger
                data-testid="booking-package"
                className={`mt-2 ${goldFocus}`}
              >
                <SelectValue placeholder="Select a package (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-ink-700 border border-white/10 text-white">
                {packages.map((p) => (
                  <SelectItem key={p.id} value={p.name} className="focus:bg-gold/15 focus:text-gold">
                    {p.name}
                  </SelectItem>
                ))}
                <SelectItem value="Not sure yet" className="focus:bg-gold/15 focus:text-gold">Not sure yet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="bk-msg" className="text-xs uppercase tracking-[0.18em] text-white/65">Message</Label>
            <Textarea
              id="bk-msg" data-testid="booking-message"
              value={form.message} onChange={handleChange("message")}
              rows={4} placeholder="Tell us about your goals, timeline, and what's in your way..."
              className={`mt-2 ${goldFocus} resize-none`}
            />
          </div>

          <button
            type="submit" disabled={submitting}
            data-testid="booking-submit"
            className="w-full mt-2 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.01] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending..." : "Send Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
